/* Sequoia Fitness | Website: https://www.sequoia.fitness | Author: Ali Hosseini | Copyright (c) 2026 sequoia.fitness. All rights reserved. */
/* Designed and developed by Pouria Shabani | https://linkedin.com/in/pouriashabani */

import { useEffect, useRef, useState } from 'react'
import { TapeSlider } from './tape.js'
import './tools-ui.css'

export const clamp = (v, min, max) => Math.min(max, Math.max(min, v))

export function normalizeDigits(value) {
  return String(value ?? '')
    .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
    .replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
}

export function ToolHead({ title, icon, badge, caption }) {
  return (
    <header className="tl-head">
      <div className="tl-title">
        <img src={icon} alt="" width="48" height="48" />
        <div>
          <h1>{title}</h1>
          {caption && <p>{caption}</p>}
        </div>
      </div>
      {badge && <span className="tl-badge">{badge}</span>}
    </header>
  )
}

function Tape({ value, min, max, step, tickWidth, majorEvery, midEvery, onChange }) {
  const pillRef = useRef(null)
  const stripRef = useRef(null)
  const sliderRef = useRef(null)
  const changeRef = useRef(onChange)
  changeRef.current = onChange
  useEffect(() => {
    const slider = new TapeSlider(pillRef.current, stripRef.current, {
      min, max, start: value, step, tickWidth, majorEvery, midEvery,
      tickBaseClass: 'tl-tick', tickMinorClass: 'is-minor', tickMidClass: 'is-mid', tickMajorClass: 'is-major',
      onChange: (v) => changeRef.current(v),
    })
    sliderRef.current = slider
    const ro = new ResizeObserver(() => slider.setValue(slider.value, false))
    ro.observe(pillRef.current)
    return () => {
      ro.disconnect()
      slider.destroy()
    }
  }, [min, max, step, tickWidth, majorEvery, midEvery])
  useEffect(() => {
    const s = sliderRef.current
    if (s && !s.dragging && !s.wheelSnapTimer && s.value !== value) s.setValue(value, false)
  }, [value])
  return (
    <div className="tl-tape" ref={pillRef} dir="ltr" style={{ "--tw": tickWidth + "px" }}>
      <div className="tl-tape__pointer" />
      <div className="tl-tape__notch" />
      <div className="tl-tape__strip" ref={stripRef} />
    </div>
  )
}

export function NumField({ label, unit, value, min, max, step = 1, onChange, hint, tint, tickWidth = 12, majorEvery = 10, midEvery = 5 }) {
  const [text, setText] = useState(String(value))
  useEffect(() => setText(String(value)), [value])
  const decimals = String(step).includes('.') ? String(step).split('.')[1].length : 0
  const snap = (n) => clamp(Math.round(n / step) * step, min, max)
  const commit = () => {
    const n = parseFloat(normalizeDigits(text))
    const next = Number.isFinite(n) ? Number(snap(n).toFixed(decimals)) : value
    onChange(next)
    setText(String(next))
  }
  const bump = (dir) => onChange(Number(snap(value + dir * step).toFixed(decimals)))
  return (
    <div className={'tl-field' + (tint ? ' tl-field--' + tint : '')}>
      <div className="tl-field__top">
        <label className="tl-field__label">{label}</label>
        {hint && <span className="tl-field__hint">{hint}</span>}
      </div>
      <div className="tl-field__row">
        <button type="button" className="tl-step" onClick={() => bump(-1)} aria-label="کم کردن">−</button>
        <div className="tl-num" dir="ltr">
          <input
            type="text"
            inputMode="decimal"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
            aria-label={label}
          />
          {unit && <span>{unit}</span>}
        </div>
        <button type="button" className="tl-step" onClick={() => bump(1)} aria-label="زیاد کردن">+</button>
      </div>
      <Tape value={value} min={min} max={max} step={step} tickWidth={tickWidth} majorEvery={majorEvery} midEvery={midEvery} onChange={onChange} />
    </div>
  )
}
